import cacheHelper from './circleHelpers/cacheHelper';
import memberHelper from './circleHelpers/memberHelper';
import circleDataHelper from './circleHelpers/circleDataHelper';
import membershipHelper from './circleHelpers/membershipHelper';

/**
 * Service for managing circles of users and their memberships
 */
class CircleService {
    /**
     * Initialize the CircleService
     */
    constructor() {
        this.tableName = 'Circle';
        this.enabled = false; // Feature flag for Circle functionality
    }

    /**
     * Clears the user cache
     */
    clearCache() {
        cacheHelper.clearCache();
    }

    /**
     * Retrieves a cached user if available
     * @param {number|string} userId - The user ID to retrieve
     * @returns {Object|null} The cached user data or null
     */
    getCachedUser(userId) {
        return cacheHelper.getCachedUser(userId);
    }

    /**
     * Stores user data in the cache
     * @param {number|string} userId - The user ID to cache
     * @param {Object} userData - The user data to store
     */
    setCachedUser(userId, userData) {
        cacheHelper.setCachedUser(userId, userData);
    }

    /**
     * Gets all active circles
     * @returns {Promise<Array>} List of active circles
     */
    getCircles() {
        if (!this.enabled) {
            console.warn('[CircleService] Circle functionality disabled');
            return Promise.resolve([]);
        }
        return circleDataHelper.getCircles();
    }

    /**
     * Creates a new circle
     * @param {Object} circleData - Data for the new circle
     * @param {string} circleData.name - The name of the circle
     * @param {string} [circleData.description] - Description of the circle
     * @param {Array|string} [circleData.members] - Members of the circle
     * @returns {Promise<Object>} The created circle record
     */
    createCircle(circleData) {
        return circleDataHelper.createCircle(circleData);
    }

    /**
     * Creates a new circle with a specific UUID (for temporary circles from invitations)
     * @param {Object} circleData - Data for the new circle
     * @param {string} circleData.name - The name of the circle 
     * @param {string} circleData.uuid - The UUID to use for the circle
     * @param {string} [circleData.description] - Description of the circle
     * @param {Array|string} [circleData.members] - Members of the circle
     * @returns {Promise<Object>} The created circle record
     */
    createCircleWithUuid(circleData) {
        return circleDataHelper.createCircleWithUuid(circleData);
    }

    /**
     * Gets all members across all circles
     * @returns {Promise<Array>} Array of all members with their circles
     */
    getAllMembers() {
        if (!this.enabled) {
            return Promise.resolve([]); // silently return empty
        }
        return memberHelper.getAllMembers();
    }

    /**
     * Gets member details by user ID
     * @param {number|string} userId - The user ID to look up
     * @returns {Promise<Object|null>} Member details including circles they belong to
     */
    getMemberByUserId(userId) {
        if (!this.enabled) return Promise.resolve(null);
        return memberHelper.getMemberByUserId(userId);
    }

    /**
     * Gets the circles a member belongs to
     * @param {number|string} userId - The user ID to look up
     * @returns {Promise<Array>} Circles the user belongs to
     */
    getCirclesByMemberId(userId) {
        return memberHelper.getCirclesByMemberId(userId);
    }

    /**
     * Gets contact details for a user
     * @param {number|string} contactUserId - The user ID to look up
     * @returns {Promise<Object|null>} Basic contact information
     */
    getMemberDetailsForContact(contactUserId) {
        return memberHelper.getMemberDetailsForContact(contactUserId);
    }

    /**
     * Gets circles that the current user belongs to
     * @param {number|string} userId - ID of the user
     * @returns {Promise<Array>} List of circles with user's role
     */
    getCurrentUserCircles(userId) {
        return membershipHelper.getCurrentUserCircles(userId);
    }

    /**
     * Gets members from circles that contain a specific user
     * @param {number|string} userId - The user ID to find shared circle members for
     * @returns {Promise<Array>} Members that share circles with the specified user
     */
    getMembersInSameCircles(userId) {
        return memberHelper.getMembersInSameCircles(userId);
    }

    /**
     * Get a circle by UUID
     * @param {string} circleId - The UUID of the circle to retrieve
     * @returns {Promise<Object>} The circle data
     */
    getCircleById(circleId) {
        return circleDataHelper.getCircleById(circleId);
    }

    /**
     * Updates an existing circle
     * @param {string} uuid - The UUID of the circle to update
     * @param {Object} circleData - New data for the circle
     * @returns {Promise<Object>} The updated circle record
     */
    updateCircle(uuid, circleData) {
        return circleDataHelper.updateCircle(uuid, circleData);
    }

    /**
     * Marks a circle as deleted
     * @param {string} circleId - UUID of the circle to delete
     * @returns {Promise<Object>} Result of the operation
     */
    softDeleteCircle(circleId) {
        return circleDataHelper.softDeleteCircle(circleId);
    }

    /**
     * Adds a member to a circle
     * @param {string} circleId - UUID of the circle to add member to
     * @param {Object} memberData - Data for the member to add
     * @returns {Promise<Object>} Updated circle record
     */
    addMember(circleId, memberData) {
        return membershipHelper.addMember(circleId, memberData);
    }

    /**
     * Removes a member from a circle
     * @param {string} circleId - UUID of the circle
     * @param {number|string} userId - ID of the user to remove
     * @returns {Promise<Object>} Updated circle record
     */
    removeMember(circleId, userId) {
        return membershipHelper.removeMember(circleId, userId);
    }

    /**
     * Updates a member's role in a circle
     * @param {string} circleId - UUID of the circle
     * @param {number|string} userId - ID of the user
     * @param {number} newRole - New role value
     * @returns {Promise<Object>} Updated circle record
     */
    updateMemberRole(circleId, userId, newRole) {
        return membershipHelper.updateMemberRole(circleId, userId, newRole);
    }

    /**
     * Check if a user has edit permission for another user
     * @param {number|string} currentUserId - The ID of the current user
     * @param {number|string} targetUserId - The ID of the user whose data would be edited
     * @returns {Promise<boolean>} Whether the current user has edit permission
     */
    hasEditPermissionForUser(currentUserId, targetUserId) {
        return membershipHelper.hasEditPermissionForUser(currentUserId, targetUserId);
    }

    /**
     * Gets the highest role a user has across all their circles
     * @param {number|string} userId - The ID of the user to check
     * @returns {Promise<number>} The highest role value (0 if not in any circles)
     */
    getCurrentUserHighestRole(userId) {
        return membershipHelper.getCurrentUserHighestRole(userId);
    }

    /**
     * Gets a list of users that the current user can assign records to
     * @param {number|string} userId - The ID of the current user
     * @returns {Promise<Array>} Array of { value: userId, label: 'First Last' } objects
     */
    getAssignableUsersFor(userId) {
        return memberHelper.getAssignableUsersFor(userId);
    }

    /**
     * Get a circle by its UUID
     * @param {string} uuid - The UUID of the circle to retrieve
     * @returns {Promise<Object|null>} The circle data or null if not found
     */
    getCircleByUuid(uuid) {
        return circleDataHelper.getCircleByUuid(uuid);
    }
}

// Export a singleton instance
const circleService = new CircleService();
export default circleService; 